// 📂 Datei: GPT_TerminalConsole.js – v2.3 final integration (kombiniert)

import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import config from "../config/config";
import AdminPageFrame from "./admin/AdminPageFrame";
import { buildAuthHeaders, getStoredIdentity } from "../utils/authSession";

const HISTORY_STORAGE_KEY = "c3po_history";
const SESSION_TIMEOUT_MINUTES = 30;

function getStoredOperatorName() {
  const { firstname, lastname, userid } = getStoredIdentity();
  return [firstname, lastname].filter(Boolean).join(" ") || userid || "Admin";
}

function getAuthHeaders() {
  return buildAuthHeaders();
}

function buildDirectoryCommand(basePath, entryName) {
  const cleanBase = String(basePath || "").replace(/\/$/, "");
  const cleanEntry = String(entryName || "");
  const targetPath = `${cleanBase}/${cleanEntry}`.replace(/\/+/g, "/");
  return cleanEntry.endsWith("/") ? `ls ${targetPath}` : `read ${targetPath}`;
}

const TerminalConsole = () => {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState(() => {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lastInteraction, setLastInteraction] = useState(Date.now());
  const scrollRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isAuthenticated && Date.now() - lastInteraction > SESSION_TIMEOUT_MINUTES * 60000) {
        resetSession("⌛ Sitzung abgelaufen (Timeout).");
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, lastInteraction]);

  useEffect(() => {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history]);

  const resetSession = (systemMessage = "") => {
    setIsAuthenticated(false);
    setInput("");
    setLoading(false);
    localStorage.removeItem(HISTORY_STORAGE_KEY);
    setHistory(systemMessage ? [systemMessage] : []);
  };

  const handleCommand = async (command) => {
    setLastInteraction(Date.now());
    setHistory((prev) => [...prev, `> ${command}`]);
    setLoading(true);
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/gpt/c3po/task`,
        { prompt: command },
        { headers: getAuthHeaders() }
      );
      const output = renderOutput(response.data?.output || response.data);
      setHistory((prev) => [...prev, output]);
    } catch (err) {
      setHistory((prev) => [...prev, "❌ Fehler bei C-3PO: " + err.message]);
    } finally {
      setLoading(false);
    }
  };

  const renderOutput = (data) => {
    if (typeof data === "string") {
      return (
        <div
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            maxWidth: "100%",
            fontFamily: "monospace",
            lineHeight: "1.5"
          }}
        >
          {data}
        </div>
      );
    }

    if (React.isValidElement(data)) return data;

    try {
      if (typeof data === "object") {
        if (data.type === "directory" && Array.isArray(data.contents)) {
          return (
            <div>
              <pre className="text-green-400 mb-2">📁 Inhalt von {data.path}</pre>
              <ul className="pl-4">
                {data.contents.map((item, idx) => (
                  <li key={idx}>
                    <button
                      className="text-green-300 hover:text-green-100 underline"
                      onClick={() =>
                        handleCommand(buildDirectoryCommand(data.path, item))
                      }
                    >
                      • {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        return (
          <pre className="overflow-auto whitespace-pre-wrap break-words">
            {JSON.stringify(data, null, 2)}
          </pre>
        );
      }
      return <pre>{String(data)}</pre>;
    } catch (err) {
      return <pre className="text-red-500">⚠️ Fehler bei der Anzeige: {String(err.message)}</pre>;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;

    if (!isAuthenticated) {
      const operatorName = trimmed || getStoredOperatorName();
      setIsAuthenticated(true);
      setLastInteraction(Date.now());
      setHistory((prev) => [
        ...prev,
        `> ${trimmed}`,
        `✅ Willkommen, ${operatorName}`,
        "💡 Tippe help fuer Kommandos, Parameter und Beispiele.",
      ]);
      setInput("");
      return;
    }

    if (trimmed.toLowerCase() === "logout") {
      resetSession("👋 Du hast dich abgemeldet.");
      return;
    }

    await handleCommand(trimmed);
    setInput("");
  };

  const getPrompt = () => (!isAuthenticated ? "Login:" : "C-3PO:");

  return (
    <AdminPageFrame
      title="GPT Terminal"
      subtitle="Direkter Admin-Zugang zu API-Server, Projektdateien, Dokumentation und Laufzeit-Logs."
      badge="AI Terminal"
    >
      <div className="min-h-[720px] p-2 text-green-400 font-mono bg-black rounded-xl">
        <div className="bg-black bg-opacity-90 p-4 rounded-xl shadow-inner max-w-6xl mx-auto">
          <h2 className="text-green-400 text-xl font-bold mb-2">C-3PO Terminal</h2>
          <div
            ref={scrollRef}
            className="border border-green-500 p-4 rounded overflow-y-auto mb-2 bg-black"
            style={{ height: "calc(100vh - 360px)" }}
          >
            {history.map((line, idx) => (
              <div
                key={idx}
                className="mb-2 whitespace-pre-wrap break-words"
              >
                {React.isValidElement(line) ? line : (
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: "100%",
                      lineHeight: "1.4",
                      fontFamily: "monospace"
                    }}
                  >
                    {String(line)}
                  </div>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="flex items-center space-x-2">
            <span className="text-green-300">{getPrompt()}</span>
            <input
              type="text"
              autoFocus
              className="flex-1 bg-black text-green-400 border-none outline-none font-mono"
              value={input}
              disabled={loading}
              placeholder={!isAuthenticated ? `z. B. ${getStoredOperatorName()}` : "help | status | get /api/system-status"}
              onChange={(e) => setInput(e.target.value)}
            />
          </form>

          <div className="mt-4 text-right">
            <Link to="/dashboard/admin">
              <button className="bg-green-800 hover:bg-green-700 text-white px-4 py-2 rounded">
                Zurueck zum Admin-Raum
              </button>
            </Link>
          </div>
        </div>
      </div>
    </AdminPageFrame>
  );
};

export default TerminalConsole;
